import { ClipboardEvent, KeyboardEvent, useCallback, useState } from 'react'

import { defaultTheme, useStyletron } from '@thanx/uikit/theme'
import { merchantTheme } from 'theme'

// Default parser: splits on common delimiters (commas, spaces, newlines, semicolons)
const parseIds = (input: string): string[] => {
  return input
    .split(/[\s,;\n]+/)
    .map(id => id.trim())
    .filter(id => id.length > 0)
}

// Default validation: alphanumeric with hyphens/underscores, must start/end with alphanumeric
const DEFAULT_VALIDATION_PATTERN = /^[a-zA-Z0-9]([a-zA-Z0-9_-]*[a-zA-Z0-9])?$/

const containerStyles = (disabled: boolean) => ({
  border: `1px solid ${merchantTheme.colors.grey30}`,
  borderRadius: '4px',
  padding: '8px',
  minHeight: '48px',
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: '4px',
  alignItems: 'center',
  backgroundColor: disabled ? merchantTheme.colors.grey10 : 'white',
  opacity: disabled ? 0.7 : 1,
})

const pillStyles = (isValid: boolean) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 8px',
  borderRadius: '16px',
  fontSize: '14px',
  backgroundColor: isValid
    ? merchantTheme.colors.belizeHole10
    : merchantTheme.colors.cayenne10,
  color: isValid
    ? merchantTheme.colors.belizeHole50
    : merchantTheme.colors.cayenne50,
  border: `1px solid ${
    isValid ? merchantTheme.colors.belizeHole50 : merchantTheme.colors.cayenne50
  }`,
})

const removeButtonStyles = (disabled: boolean) => ({
  marginLeft: '4px',
  background: 'none',
  border: 'none',
  cursor: disabled ? 'not-allowed' : 'pointer',
  padding: '0 2px',
  fontSize: '16px',
  lineHeight: 1,
  color: 'inherit',
  ':hover': {
    opacity: disabled ? 1 : 0.7,
  },
})

const inputStyles = {
  border: 'none',
  outline: 'none',
  flex: 1,
  minWidth: '120px',
  fontSize: '14px',
  padding: '4px',
  backgroundColor: 'transparent',
  '::placeholder': {
    color: defaultTheme.colors.grey50,
  },
}

type PillInputProps = {
  name: string
  selectedIds: string[]
  setValue: (ids: string[]) => void
  className?: string
  disabled?: boolean
  placeholder?: string
  validationPattern?: RegExp
  invalidTooltip?: string
  removeAriaLabel?: string
}

export const PillInput = ({
  name,
  selectedIds,
  setValue,
  className = '',
  disabled = false,
  placeholder = '',
  validationPattern = DEFAULT_VALIDATION_PATTERN,
  invalidTooltip = 'Invalid ID',
  removeAriaLabel = 'Remove',
}: PillInputProps) => {
  const [css] = useStyletron()
  const [inputValue, setInputValue] = useState('')

  const addIds = useCallback(
    (ids: string[]) => {
      const newIds = ids.filter(
        id => id.length > 0 && !selectedIds.includes(id)
      )
      if (newIds.length > 0) {
        setValue([...selectedIds, ...newIds])
      }
    },
    [selectedIds, setValue]
  )

  const processAndClearInput = useCallback(
    (text: string) => {
      const ids = parseIds(text)
      addIds(ids)
      setInputValue('')
    },
    [addIds]
  )

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return
      if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
        e.preventDefault()
        processAndClearInput(inputValue)
      } else if (
        e.key === 'Backspace' &&
        inputValue === '' &&
        selectedIds.length > 0
      ) {
        setValue(selectedIds.slice(0, -1))
      }
    },
    [inputValue, processAndClearInput, selectedIds, setValue, disabled]
  )

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      if (disabled) return
      e.preventDefault()
      const pastedText = e.clipboardData.getData('text')
      processAndClearInput(pastedText)
    },
    [processAndClearInput, disabled]
  )

  const handleInputBlur = useCallback(() => {
    if (inputValue.trim()) {
      processAndClearInput(inputValue)
    }
  }, [inputValue, processAndClearInput])

  const removeId = useCallback(
    (idToRemove: string) => {
      if (disabled) return
      setValue(selectedIds.filter(id => id !== idToRemove))
    },
    [selectedIds, setValue, disabled]
  )

  return (
    <div className={`${className} ${css(containerStyles(disabled))}`}>
      {selectedIds.map(id => {
        const isValid = validationPattern.test(id.trim())
        return (
          <span
            key={id}
            className={css(pillStyles(isValid))}
            title={isValid ? undefined : invalidTooltip}
          >
            {id}
            <button
              type="button"
              onClick={() => removeId(id)}
              disabled={disabled}
              className={css(removeButtonStyles(disabled))}
              aria-label={removeAriaLabel}
            >
              ×
            </button>
          </span>
        )
      })}
      <input
        type="text"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={handleInputKeyDown}
        onPaste={handlePaste}
        onBlur={handleInputBlur}
        placeholder={selectedIds.length === 0 ? placeholder : ''}
        disabled={disabled}
        className={css(inputStyles)}
        aria-labelledby={name}
      />
    </div>
  )
}
